/**
 * Order, filters and sorting of the Price limits list (CU-869fkbjdh).
 *
 * "As the engine reads them": date protections first (they sit on top of
 * everything), then ordinary rules from most to least specific — by the
 * **server's** `specificity` column, never recomputed here (M7): the one
 * ranking is `limits.ranked` in the engine, `(-specificity, str(id))`.
 * Amounts never take part, so typing a price cannot move a row.
 *
 * Pure: no React, no catalogue. The labels and the matrices come in as
 * context. `node --test`.
 */
import { isBinding, kindOf, whereChips, whenLines } from './priceLimitsProjection.js'

export const EMPTY_FILTERS = {
	search: '', kind: 'all',
	date_from: '', date_to: '',
	office_scope: '*', category_scope: '*', duration_unit: '*',
	franchise_scope: '*', tariff_scope: '*', insurance_scope: '*',
}

export const KIND_CHIPS = [
	{ key: 'all', label: 'All' },
	{ key: 'floor', label: 'Floors' },
	{ key: 'matrix', label: 'Matrices' },
	{ key: 'protection', label: 'Protections' },
]

//  value ↔ select key, as `axes.jsx` does. Never `||`, never `!v`:
//  `false` is Non Prepaid and `0` would be a legitimate id.
const idKey      = (v) => (v == null ? '' : String(v))
const prepaidKey = (v) => (v == null ? '' : (v ? '1' : '0'))

const byId = (a, b) => String(a.id).localeCompare(String(b.id))

/**
 * Protections by `date_from` ascending (nulls last) then `String(id)`;
 * ordinary rules by the server's `specificity` descending then
 * `String(id)` ascending. A row without a server specificity (one just
 * imported, whose POST has not come back) waits **first** in its block —
 * transitory until the reload after the import; the engine would rank it
 * last (`or 0`). Not an order the screen asserts, only a place to wait.
 */
export function engineOrder(rules) {
	const prot = rules.filter(r => kindOf(r) === 'protection').sort((a, b) => {
		const x = a.date_from || null, y = b.date_from || null
		if (x !== y) {
			if (x == null) return 1
			if (y == null) return -1
			return x < y ? -1 : 1
		}
		return byId(a, b)
	})
	const spec = (r) => (r.specificity == null ? Infinity : Number(r.specificity))
	const ord = rules.filter(r => kindOf(r) !== 'protection').sort((a, b) => {
		// `Infinity − Infinity` is NaN: two rows both waiting fall through to the id.
		const d = spec(b) - spec(a)
		return Number.isNaN(d) || d === 0 ? byId(a, b) : d
	})
	return [...prot, ...ord]
}

/** `{ all, floor, matrix, protection }` — the kind facets; the three add up to `all`. */
export function countsByKind(rules) {
	const c = { all: rules.length, floor: 0, matrix: 0, protection: 0 }
	for (const r of rules) c[kindOf(r)] += 1
	return c
}

/**
 * `ctx = { labels, matrixById }`. The search runs over the projection —
 * the words the operator sees (chips, the When lines, the protection's
 * name, the matrix's name) — not over the stored codes.
 */
export function applyFilters(rules, f, ctx = {}) {
	const labels = ctx.labels || {}
	const matrixById = ctx.matrixById || new Map()
	const filterFrom = f.date_from || null
	const filterTo   = f.date_to   || null
	const q = (f.search || '').trim().toLowerCase()
	return rules.filter(r => {
		if (f.kind && f.kind !== 'all' && kindOf(r) !== f.kind) return false
		// Date-range overlap: a rule with null dates ("applies always") is
		// counted as overlapping any filter range. Otherwise both endpoints
		// are treated as inclusive; an unbounded rule endpoint counts as
		// −∞ / +∞ respectively.
		if (filterFrom || filterTo) {
			const rFrom = r.date_from || null, rTo = r.date_to || null
			if (rFrom || rTo) {
				if (filterFrom && rTo && rTo < filterFrom) return false
				if (filterTo && rFrom && rFrom > filterTo) return false
			}
		}
		if (q) {
			const w = whenLines(r)
			const hay = [
				...whereChips(r, labels).map(c => c.text),
				w.title, w.dates,
				r.date_from, r.date_to, r.name,
				isBinding(r) ? (matrixById.get(String(r.matrix_id))?.name || '') : '',
			].filter(Boolean).join(' ').toLowerCase()
			if (!hay.includes(q)) return false
		}
		// Compared as keys, never as values: `scope_is_prepaid === false`
		// has to survive the comparison, and `'' !== null` would drop it.
		if (f.franchise_scope && f.franchise_scope !== '*'
		    && idKey(r.scope_ps_provider_id) !== f.franchise_scope.slice('franchise:'.length)) return false
		if (f.tariff_scope && f.tariff_scope !== '*'
		    && prepaidKey(r.scope_is_prepaid) !== f.tariff_scope.slice('tariff:'.length)) return false
		if (f.insurance_scope && f.insurance_scope !== '*'
		    && idKey(r.scope_ps_insurance_id) !== f.insurance_scope.slice('insurance:'.length)) return false
		if (f.office_scope && f.office_scope !== '*') {
			const [kind, code] = f.office_scope.split(':')
			if (kind === 'office' && !(r.scope_offices || []).includes(code)) return false
			if (kind === 'group' && r.scope_office_group !== code) return false
		}
		if (f.category_scope && f.category_scope !== '*') {
			const [kind, code] = f.category_scope.split(':')
			if (!(r.scope_categories || []).some(c => c.type === kind && c.code === code)) return false
		}
		// Whether the rule scopes duration at all — there is only one unit now.
		if (f.duration_unit === 'null' && r.scope_duration) return false
		if (f.duration_unit === 'days' && !r.scope_duration) return false
		return true
	})
}

/**
 * `sort = { key: 'where' | 'when' | 'floor' | 'ceiling', dir: 'asc' | 'desc' }`.
 * Where by the chips' text; When with protections first, then `date_from`;
 * Floor by the amount (a binding by the price of its first base band);
 * Ceiling by the amount, nulls last — "sorting by Ceiling groups the rules
 * that have one" (criterion 7).
 */
export function sortRows(rows, sort, ctx = {}) {
	if (!sort) return rows
	const labels = ctx.labels || {}
	const matrixById = ctx.matrixById || new Map()
	const dir = sort.dir === 'desc' ? -1 : 1
	const key = {
		where:   (r) => whereChips(r, labels).map(c => c.text).join(' ') || '~',
		when:    (r) => `${r.protects_dates ? '0' : '1'}|${r.date_from || '9999'}`,
		floor:   (r) => (isBinding(r) ? (matrixById.get(String(r.matrix_id))?.base?.[0]?.price ?? null) : r.min_price),
		ceiling: (r) => (isBinding(r) || r.protects_dates ? null : r.max_price),
	}[sort.key]
	if (!key) return rows
	const numeric = sort.key === 'floor' || sort.key === 'ceiling'
	return [...rows].sort((a, b) => {
		const x = key(a), y = key(b)
		if (numeric) {
			if (x == null && y == null) return 0
			if (x == null) return 1
			if (y == null) return -1
			return dir * (Number(x) - Number(y))
		}
		return dir * String(x).localeCompare(String(y))
	})
}
